import {
  Alert,
  Box,
  Checkbox,
  TextField,
  Typography,
  useTheme,
} from "@outschool/backpack";
import {
  GetMeetingRecapQuery,
  GetMeetingRecapQueryVariables,
  TeacherMeetingRecapEnabledSettingQuery,
  UpdateMeetingRecapMutation,
  UpdateMeetingRecapMutationVariables,
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { sectionNoteMakerPath } from "@outschool/routes";
import { dayjs } from "@outschool/time";
import { useTrackEvent } from "@outschool/ui-analytics";
import {
  gql,
  useMutation,
  useQueryWithPreviousData,
} from "@outschool/ui-apollo";
import { TrackedButton } from "@outschool/ui-components-shared";
import { Modal } from "@outschool/ui-legacy-component-library";
import { useLinkComponent, useNavigation } from "@outschool/ui-utils";
import _ from "lodash";
import React, { useCallback, useEffect } from "react";

type ClassRecapPreviewProps = {
  meetingUid: string;
  sectionUid: string;
  meetingEndTime: Date;
  withNoteMakerMessage?: boolean;
  hideEditMarketingSettings?: boolean;
};

const marketingSettingsQuery = gql`
  query TeacherMeetingRecapEnabledSetting {
    teacherMarketingSettings {
      ai_meeting_summary_is_on
    }
  }
`;

export const meetingRecapQuery = gql`
  query GetMeetingRecap($meetingUid: ID!) {
    meetingAiSummary(meetingUid: $meetingUid) {
      meeting_uid
      ai_summary_text
      edited_summary_text
      is_skipped
      sent_at
      deleted_at
    }
  }
`;

export const updateMeetingRecapMutation = gql`
  mutation UpdateMeetingRecap(
    $meetingUid: ID!
    $input: MeetingAiSummaryPatchInput!
  ) {
    updateMeetingAiSummary(meetingUid: $meetingUid, input: $input) {
      edited_summary_text
      is_skipped
    }
  }
`;

export default function ClassRecapPreview({
  meetingUid,
  sectionUid,
  meetingEndTime,
  withNoteMakerMessage,
  hideEditMarketingSettings,
}: ClassRecapPreviewProps) {
  const { t } = useTranslation(
    "ui-components-classroom\\classroom\\ClassRecapPreview",
  );
  const navigate = useNavigation();
  const Link = useLinkComponent();
  const trackEvent = useTrackEvent();

  const { data: marketingSettingsData, loading: marketingSettingsLoading } =
    useQueryWithPreviousData<TeacherMeetingRecapEnabledSettingQuery>(
      marketingSettingsQuery,
      {
        fetchPolicy: "no-cache",
      },
    );
  const { data, loading, error } = useQueryWithPreviousData<
    GetMeetingRecapQuery,
    GetMeetingRecapQueryVariables
  >(meetingRecapQuery, {
    variables: {
      meetingUid,
    },
  });
  const [mutate] = useMutation<
    UpdateMeetingRecapMutation,
    UpdateMeetingRecapMutationVariables
  >(updateMeetingRecapMutation);

  const [isSkipped, setIsSkipped] = React.useState(false);
  const [editedSummaryText, setEditedSummaryText] = React.useState<
    string | null
  >(null);
  const [isUpdating, setIsUpdating] = React.useState(false);
  useEffect(() => {
    const summaryTextPreview =
      data?.meetingAiSummary?.edited_summary_text ||
      data?.meetingAiSummary?.ai_summary_text;
    setEditedSummaryText(summaryTextPreview || null);
    setIsSkipped(data?.meetingAiSummary?.is_skipped || false);
  }, [data]);

  const handleSaveEdits = useCallback(
    async (editedSummaryText: string) => {
      const summaryTextPreview =
        data?.meetingAiSummary?.edited_summary_text ||
        data?.meetingAiSummary?.ai_summary_text;
      trackEvent("preview_ai_meeting_summary_edit_submit", {
        summaryText: summaryTextPreview,
        editedSummaryText: editedSummaryText,
      });
      setIsUpdating(true);
      const resp = await mutate({
        variables: {
          meetingUid,
          input: {
            edited_summary_text: editedSummaryText,
          },
        },
      });
      if (resp.errors) {
        OsPlatform.captureError(resp.errors);
      }
      setIsUpdating(false);
      setModalIsOpen(false);
    },
    [mutate, meetingUid, data, trackEvent],
  );
  const handleToggleSkipMessage = useCallback(
    async (isSkipped: boolean) => {
      const summaryTextPreview =
        data?.meetingAiSummary?.edited_summary_text ||
        data?.meetingAiSummary?.ai_summary_text;
      trackEvent("preview_ai_meeting_summary_skip_click", {
        summaryText: summaryTextPreview,
      });
      setIsSkipped(isSkipped);
      setIsUpdating(true);
      const resp = await mutate({
        variables: {
          meetingUid,
          input: {
            is_skipped: isSkipped,
          },
        },
      });
      if (resp.errors) {
        OsPlatform.captureError(resp.errors);
      }
      setIsUpdating(false);
    },
    [mutate, meetingUid, data, trackEvent],
  );

  const [modalIsOpen, setModalIsOpen] = React.useState(false);
  const theme = useTheme();
  const disabledByMarketingSettings =
    marketingSettingsData?.teacherMarketingSettings
      ?.ai_meeting_summary_is_on === false;
  if (
    loading ||
    error ||
    data?.meetingAiSummary === null ||
    data?.meetingAiSummary?.sent_at ||
    marketingSettingsLoading ||
    disabledByMarketingSettings
  ) {
    return <></>;
  }
  // assume summary is still processing if there's no data and <2 hours after meeting end
  const processingTimeUpperBound = dayjs(meetingEndTime).add(2, "hours");
  const isProcessingSummary =
    !loading &&
    !data?.meetingAiSummary &&
    dayjs().isBefore(processingTimeUpperBound);
  const marketingSettingsCopy = hideEditMarketingSettings ? (
    <Typography variant="body1">
      {t(
        "Organization administrators can opt out of auto-recaps for the organization in marketing settings.",
      )}
    </Typography>
  ) : (
    <>
      <Typography variant="body1">
        {t("Turn off all auto-recaps")}&nbsp;
      </Typography>
      <Link to={"/teach/marketing"}>{t("here")}</Link>
    </>
  );

  return (
    <Box>
      <Alert
        severity="info"
        title={t("Auto Parent Update")}
        sx={{
          backgroundColor: (theme.colors as any).waterslide100,
          border: "none",
        }}
      >
        <Box flex sx={{ flexDirection: "column", gap: "1em" }}>
          <Typography variant="body1">
            {t(
              "Outschool automatically prepares a recap of what the class covered. This will message all parents 4 hours after class ends and saves you time while keeping parents updated. Outschool educators who keep parents updated tend to have longer-lasting engagements.",
            )}
          </Typography>
          <TrackedButton
            onClick={() => setModalIsOpen(true)}
            variant="contained"
            color="primary"
            sx={{ width: "fit-content" }}
            disabled={isUpdating || isProcessingSummary}
          >
            {t("Manage Message")}
          </TrackedButton>
          {isProcessingSummary && (
            <Typography variant="body1">
              {t(
                "Still processing. We’ll send you a notification once ready (often 15 minutes after meeting ends).",
              )}
            </Typography>
          )}

          <Modal open={modalIsOpen} onClose={() => setModalIsOpen(false)}>
            <Box flex sx={{ flexDirection: "column", gap: "1em" }}>
              <Typography variant="h4" gutterBottom>
                {t("Quick Parent Update")}
              </Typography>
              <Box flex>
                <Typography variant="h6">
                  {t("Edit meeting recap ")}&nbsp;
                </Typography>
                <Typography variant="h6" sx={{ color: "grey.500" }}>
                  {t("(optional)")}
                </Typography>
              </Box>
              <Typography variant="body1" gutterBottom>
                {t(
                  "This class recap is sent to all learners who attended this section. We will add a greeting at the top of this text automatically.",
                )}
              </Typography>
              <TextField
                fullWidth
                multiline
                maxRows={15}
                value={editedSummaryText}
                inputProps={{ maxLength: 10_000 }}
                onChange={(
                  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                ) => setEditedSummaryText(e.target.value)}
              />
              <Box>
                <Checkbox
                  checked={isSkipped}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleToggleSkipMessage(e.target.checked)
                  }
                  color="primary"
                  disabled={isUpdating}
                  sx={{ paddingLeft: 0 }}
                />
                <Typography variant="body1">
                  {t("Skip sending this auto-recap")}
                </Typography>
              </Box>
              {isSkipped && <Box>{marketingSettingsCopy}</Box>}
              <Box flex sx={{ justifyContent: "space-between" }}>
                <TrackedButton
                  onClick={() => setModalIsOpen(false)}
                  variant="outlined"
                  disabled={isUpdating}
                >
                  {t("Cancel")}
                </TrackedButton>
                {!isSkipped && (
                  <TrackedButton
                    onClick={() =>
                      editedSummaryText && handleSaveEdits(editedSummaryText)
                    }
                    variant="contained"
                    color="primary"
                    disabled={
                      isUpdating || _.isEmpty(editedSummaryText) || isSkipped
                    }
                  >
                    {t("Update")}
                  </TrackedButton>
                )}
              </Box>
            </Box>
          </Modal>
        </Box>
      </Alert>
      <Box>
        {withNoteMakerMessage && (
          <Box
            flex
            sx={{ alignItems: "center", paddingTop: "1em", gap: "1em" }}
          >
            <Typography variant="body1">
              {t(
                "Or, write your own quick note summarizing a specific learner’s progress and homework. If you message a specific learner, they will not get the auto-recap above.",
              )}
            </Typography>
            <TrackedButton
              onClick={() => navigate(sectionNoteMakerPath(sectionUid))}
              sx={{ height: "fit-content" }}
            >
              {t("Leave a Note")}
            </TrackedButton>
          </Box>
        )}
      </Box>
    </Box>
  );
}
